/**
 * LDT slide scanner config for the shared SlideImageSection.
 * LDT supports only .svs, .ndpi, .mrxs_zip, .zip, and four scanner types (Grundium Ocus 20,
 * Hamamatsu Nanozoomer S360, Leica Aperio GT450 DX, 3D Histech P1000). No instructions PDF URL.
 */
import type { SlideScannerConfig } from 'src/distribution_platforms/features/types/slide_scanner';
import {
  SCANNER_SUPPORTED_EXTENSIONS,
  SCANNER_TYPE_OPTIONS,
} from 'src/distribution_platforms/features/utils/slide_scanner_constants';
import { SlideImageScannerType } from 'src/types';
import { WorkflowType } from 'src/types/workflows';
import { getFileExtension } from 'src/utils';

/** Scanners supported for LDT slide upload (e.g. lab tech): .svs, .ndpi, .mrxs_zip, .zip only. */
const LDT_SUPPORTED_SCANNERS = new Set<SlideImageScannerType>([
  SlideImageScannerType.GrundiumOcus_20,
  SlideImageScannerType.HamamatsuNanozoomerS360,
  SlideImageScannerType.LeicaGt450Dx,
  SlideImageScannerType.ThreeDHistechP1000,
]);

function getLdtSupportedExtensions(): string[] {
  const set = new Set<string>();
  for (const scanner of LDT_SUPPORTED_SCANNERS) {
    const exts = SCANNER_SUPPORTED_EXTENSIONS[scanner];
    for (const ext of exts) {
      set.add(ext);
    }
  }
  return Array.from(set).sort();
}

/**
 * Returns slide scanner config for LDT Direct Upload Lab workflow (e.g. lab tech order detail).
 * Only DIRECT_UPLOAD_LAB is supported; other workflows throw.
 */
export function getLdtSlideScannerConfig(
  workflowType: WorkflowType,
): SlideScannerConfig {
  if (workflowType !== WorkflowType.DIRECT_UPLOAD_LAB) {
    throw new Error(
      'Slide scanner config is only supported for Direct Upload Lab workflow.',
    );
  }
  const supportedExtensions = getLdtSupportedExtensions();
  return {
    instructionsPdfUrl: null,
    scannerOptions: SCANNER_TYPE_OPTIONS.filter((opt) =>
      LDT_SUPPORTED_SCANNERS.has(opt.value),
    ),
    supportedExtensions,
    supportedFileTypesText: supportedExtensions
      .map((ext) => ext.toUpperCase())
      .join(', '),
    validateScannerFileCompatibility: (filename, scannerType) => {
      if (!scannerType) {
        return 'Please select a scanner type';
      }
      const extension = getFileExtension(filename);
      const supportedForScanner = SCANNER_SUPPORTED_EXTENSIONS[scannerType];
      if (!supportedForScanner?.has(extension)) {
        const supportedList = supportedForScanner
          ? Array.from(supportedForScanner).join(', ')
          : '';
        return `File extension "${extension}" is not supported by the selected scanner. Supported extensions: ${supportedList}`;
      }
      return null;
    },
  };
}
